import React, { useState, useEffect } from 'react'
import { Popover, Col, Row } from 'antd'
import { BBDTag, Texto } from '@gravitate-js/excalibrr'
import { FileSyncOutlined, FileTextOutlined } from '@ant-design/icons'
import { ReadableDifference, titleCase } from '@utils/general'
import api from '@utils/api'
import AssignedToDriver from '@assets/CustomIcons/orderStatusIcons/assigned_not_started.svg'
import AssignedToCarrier from '@assets/CustomIcons/orderStatusIcons/assigned_carrier.svg'
import Driving from '@assets/CustomIcons/orderStatusIcons/steering-wheel.svg'
import Loading from '@assets/CustomIcons/orderStatusIcons/loading-icon.svg'
import Dropping from '@assets/CustomIcons/orderStatusIcons/dropping-icon.svg'
import ArrivedAtDrop from '@assets/CustomIcons/orderStatusIcons/arrived_at_drop.svg'
import ArrivedAtLoad from '@assets/CustomIcons/orderStatusIcons/arrived_at_load.svg'
import moment from 'moment'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { TripStatusIcon } from '@constants/TripStatusIcons'

export const TankOrderStatusPopover = ({ status, orderId }) => {
  const [popoverContent, setPopoverContent] = useState()
  const getTankOrderStatus = (status) => {
    switch (status) {
      case 'unset':
        return <FileTextOutlined style={{ fontSize: '1.2em' }} />
      case 'assigned_to_driver':
        return <img src={AssignedToDriver} />
      case 'assigned_to_carrier':
        return <img src={AssignedToCarrier} />
      case 'loading':
        return <img src={Loading} />
      case 'driving to load':
        return <img src={Driving} />
      case 'driving to drop':
        return <img src={Driving} />
      case 'arrived at load':
        return <img src={ArrivedAtLoad} />
      case 'arrived at drop':
        return <img src={ArrivedAtDrop} />
      case 'dropping':
        return <img src={Dropping} />
      case 'preview':
        return <FileSyncOutlined style={{ fontSize: '1.2em' }} />
      default:
        return null
    }
  }
  if (status === 'deleted' || status === 'complete') {
    return null
  }
  return (
    <div className='flex justify-center items-center'>
      {status && status !== 'unset' && (
        <Popover
          title={<OrderStatusPopoverTitle popoverContent={popoverContent} status={status} />}
          placement='top'
          content={
            <OrderStatusPopoverContent
              status={status}
              orderId={orderId}
              getTankOrderStatus={getTankOrderStatus}
              popoverContent={popoverContent}
              setPopoverContent={setPopoverContent}
            />
          }
        >
          <BBDTag style={{ display: 'flex', alignItems: 'center' }} className='pb-0' theme2>
            <Texto className='pb-0' appearance='primary' style={{ fontSize: 16, lineHeight: 1 }} weight='bold'>
              <TripStatusIcon status={status} />
            </Texto>
          </BBDTag>
        </Popover>
      )}
      {status === 'unset' && <FileTextOutlined />}
    </div>
  )
}

const OrderStatusPopoverTitle = ({ popoverContent, status }) => {
  const showLocation =
    status === 'loading' ||
    status === 'driving to load' ||
    status === 'dropping' ||
    status === 'driving to drop' ||
    status === 'arrived at load' ||
    status === 'arrived at drop' ||
    status === 'preview'

  return (
    <div className='flex justify-sb'>
      <Texto category='h6'>Order Status</Texto>
      {showLocation && <Texto className='pt-1'>{popoverContent?.location_name}</Texto>}
    </div>
  )
}

const OrderStatusPopoverContent = ({ status, orderId, getTankOrderStatus, popoverContent, setPopoverContent }) => {
  const assignedToCarrier = status === 'assigned_to_carrier'
  useEffect(() => {
    let isMounted = true
    api
      .getOrderStatus(orderId)
      .then((resp) => {
        isMounted && setPopoverContent(resp.data)
      })
      .catch((e) => {
        console.log(e, 'error getting store_monitor/order_status')
      })
    return () => (isMounted = false)
  }, [orderId])

  return (
    <>
      <Row style={{ flexWrap: 'nowrap' }}>
        <Col flex={1} className='m-1 p-1'>
          <Texto category='label'>{assignedToCarrier ? 'Carrier Start' : 'Estimated Delivery'}</Texto>
          <Texto weight='bold' category='p1'>
            {popoverContent && assignedToCarrier
              ? formatters.DATE_TIME(popoverContent?.estimated_start)
              : formatters.DATE_TIME(popoverContent?.estimated_end)}
          </Texto>
        </Col>
        <Col className='m-1 p-2'>
          <Texto align='right' category='label'>
            {assignedToCarrier ? 'Carrier End' : 'ETA'}
          </Texto>
          <Texto align='right' weight='bold' category='p1'>
            {popoverContent && assignedToCarrier
              ? formatters.DATE_TIME(popoverContent?.estimated_end)
              : ReadableDifference(popoverContent?.estimated_end, undefined, false)}
          </Texto>
        </Col>
      </Row>
      <Row style={{ flexWrap: 'nowrap' }} align='middle'>
        <Col flex={1} className='m-1 p-1'>
          <Texto category='label'>Current Status</Texto>
          <div className='flex items-center'>
            <div className='mt-2'>
              <Texto category='p2' appearance='primary' className='mr-2'>
                <TripStatusIcon status={status} />
              </Texto>
            </div>
            <Texto
              weight='bold'
              className='ml-1 pt-1'
              category='p1'
              appearance={
                status === 'loading' || status === 'arrived_at_load'
                  ? 'secondary'
                  : status === 'dropping' || status === 'arrived_at_drop'
                    ? 'theme3'
                    : 'default'
              }
            >
              {status ? titleCase(status) : 'Not Assigned'}
            </Texto>
          </div>
        </Col>
        <Col flex={1} className='m-1 p-1'>
          <Texto align='right' category='label'>
            Assigned To:
          </Texto>
          <Texto align='right' weight='bold' category='p1'>
            {popoverContent && popoverContent?.assigned_to}
          </Texto>
        </Col>
      </Row>
    </>
  )
}
